import { HttpErrorResponse } from '@angular/common/http';
import { DestroyRef, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, catchError, tap, throwError } from 'rxjs';
import { DataService } from '../../shared/services/data.service';
import { LanguageService } from '../../shared/services/language.service';
import { BasePage } from '../helpers/models/base-page.model';
import { ImageComponent } from '../models/image.model';
import { Result } from '../models/result';
import { mapContentTypeToTemplate } from '../services/google-tag-manager/template-map';
import { CURRENT_TEMPLATE } from '../services/google-tag-manager/use-current-template';
import { SeoService } from './seo.service';

// TODO: use composition over inheritance
export class BaseDataService<T extends BasePage> {
  public controller!: string;
  public content = signal<T>({} as T);
  public components = signal<any[]>([]);
  public bilingualPaths = signal<{ [key: string]: string }>({});
  private contentSubject: BehaviorSubject<T> = new BehaviorSubject<T>({} as T);
  public content$: Observable<T> = this.contentSubject.asObservable();

  #router = inject(Router);
  #dataService = inject(DataService);
  #seoService = inject(SeoService);
  #languageService = inject(LanguageService);
  #currentTemplate = inject(CURRENT_TEMPLATE);

  readonly #destroy = inject(DestroyRef).onDestroy(() => {
    this.#seoService.deleteBreadcrumbSchema();
  });

  public get(controller?: string): Observable<Result<T>> | any {
    const originUrl = window.location.pathname;
    return this.dataHandling(this.#dataService.get<Result<T>>(controller ?? this.controller)).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 500) {
          const languages = this.#languageService.languagesList().map((lang) => lang.prefix);
          const urlParts = new URL(window.location.href).pathname.split('/').filter((part) => part);
          const langPrefix = languages.includes(urlParts[0]) ? urlParts.shift() || '' : '';
          if (urlParts.length > 2) {
            this.#router.navigate([langPrefix, urlParts[0]]);
          } else {
            this.#router.navigate(['not-found']);
          }
        }
        if (error.status === 404) {
          const languages = this.#languageService.languagesList().map((lang) => lang.prefix);
          const urlParts = originUrl.split('/').filter((part) => part);
          const langPrefix = languages.includes(urlParts[0]) ? urlParts[0] : 'he';

          this.#router.navigate(['not-found'], {
            queryParams: { from: originUrl }
          });
        }
        return throwError(() => new Error(error.message));
      })
    );
  }

  isResult<T extends BasePage>(data: any): data is Result<T> {
    return data && typeof data === 'object' && 'content' in data;
  }

  public dataHandling(call: Observable<Result<T>> | any): Observable<Result<T>> | any {
    return call
      .pipe(
        tap((data: Result<T> | any) => {
          const target = this.isResult<T>(data) ? data.content : data;

          const image = target?.image as ImageComponent | undefined;
          const aliasOGType = target?.contentType === 'article' ? 'ARTICLE' : 'WEBSITE';
          if (target?.contentType) {
            this.#seoService.setContentType(target.contentType);
            this.#currentTemplate.set(mapContentTypeToTemplate(target?.contentType));
          }
          this.bilingualPaths = target.bilingualPaths;
          target?.seoData && this.#seoService.setTags(target.seoData, image, aliasOGType);
          target?.bilingualPaths && this.#seoService.setAlternateLinks(target.bilingualPaths);
          target?.breadcrumbs && this.#seoService.setBreadcrumbSchema(target.breadcrumbs);
          
          this.#seoService.ensureCanonicalTag(target?.seoData);
        })
      )
      .pipe(
        catchError((error) => {
          console.error('error', error);
          // this.#router.navigate(['not-found']);
          return throwError(error);
        })
      );
  }
}
